package com.schoolconnect.app.feature.exams

import androidx.compose.foundation.BorderStroke
import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.foundation.border
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.schoolconnect.app.designsystem.*
import com.schoolconnect.app.designsystem.AppPageHeader
import com.schoolconnect.app.designsystem.AppPageSubtitle
import com.schoolconnect.app.designsystem.list.*
import com.schoolconnect.app.feature.common.*
import ischoolconnect.sharedui.generated.resources.*
import org.jetbrains.compose.resources.painterResource

@Composable
fun ExamsScreen(
    state: ExamsUiState,
    onEvent: (ExamsUiEvent) -> Unit,
    onBack: () -> Unit,
    shellRoleCode: String = "",
    shellSchoolLabel: String = "iConnect.School",
    onChangeRole: () -> Unit = onBack,
    onLogout: () -> Unit = {}
) {
    var profileExpanded by remember { mutableStateOf(false) }
    var rowsMenuExpanded by remember { mutableStateOf(false) }
    var addDialogVisible by remember { mutableStateOf(false) }
    var editingExam by remember { mutableStateOf<ExamRowUi?>(null) }
    var deleteTarget by remember { mutableStateOf<ExamRowUi?>(null) }

    // Search, status, class, sorting, and totalCount all come from the server
    // page. Client-side filtering here would make the page footer and results
    // incorrect once there are more records than the current page.
    val pageItems = state.rows
    val window = pageWindow(state.pageIndex, state.pageSize, state.totalCount)
    val columns = examColumns()

    AdminListPageScaffold(
        title = "Exams",
        subtitle = "Create exams, enter results, publish them and view exam insights.",
        backgroundColor = AppBackground,
        titleColor = AppPageHeader,
        subtitleColor = AppPageSubtitle,
        headerActions = {
            ShellTopRightActions(shellRoleLabel(shellRoleCode), shellRoleSubtitle(shellRoleCode), shellAvatarInitials(shellRoleLabel(shellRoleCode)), profileExpanded, { profileExpanded = it }, onChangeRole, onLogout)
        },
        summaryCards = {
            if (state.isStatisticsLoading) {
                ExamsSummaryCardsShimmer()
            } else if (state.statisticsErrorMessage.isNotBlank()) {
                Text(state.statisticsErrorMessage, color = AppMuted, fontSize = 12.sp)
            } else {
                ExamMetric("Total Exams", state.totalExams.toString(), "Across assigned classes", Res.drawable.book, AppPrimary, AppPrimarySoft)
                ExamMetric("Upcoming", state.upcomingCount.toString(), "Scheduled exams", Res.drawable.calendar_grid, AppPrimary, AppPrimarySoft)
                ExamMetric("Ongoing", state.ongoingCount.toString(), "In progress", Res.drawable.clock, AppWarning, Color(0xFFFFF7E6))
                ExamMetric("Completed", state.completedCount.toString(), "Completed exams", Res.drawable.circle_check, AppSuccess, AppSuccessSoft)
            }
        },
        toolbar = { ExamFilterPanel(state, onEvent) },
        table = { modifier ->
            AdminListTableCard(
                modifier = modifier,
                minContentWidth = 1320.dp,
                header = { AdminListSortHeaderRow(listOf(
                    AdminListSortColumn("Exam ID", 1f, state.sortField == ExamsSortField.ID, state.sortAscending) { onEvent(ExamsUiEvent.SortChanged(ExamsSortField.ID)) },
                    AdminListSortColumn("Exam Name", 1.55f, state.sortField == ExamsSortField.NAME, state.sortAscending) { onEvent(ExamsUiEvent.SortChanged(ExamsSortField.NAME)) },
                    AdminListSortColumn("Exam Type", 1.2f, state.sortField == ExamsSortField.TYPE, state.sortAscending) { onEvent(ExamsUiEvent.SortChanged(ExamsSortField.TYPE)) },
                    AdminListSortColumn("Classes", 1.35f, state.sortField == ExamsSortField.CLASSES, state.sortAscending) { onEvent(ExamsUiEvent.SortChanged(ExamsSortField.CLASSES)) },
                    AdminListSortColumn("Start Date", 1.05f, state.sortField == ExamsSortField.START_DATE, state.sortAscending) { onEvent(ExamsUiEvent.SortChanged(ExamsSortField.START_DATE)) },
                    AdminListSortColumn("Results Entered", 1.05f, state.sortField == ExamsSortField.RESULTS_ENTERED, state.sortAscending) { onEvent(ExamsUiEvent.SortChanged(ExamsSortField.RESULTS_ENTERED)) },
                    AdminListSortColumn("Status", .9f, state.sortField == ExamsSortField.STATUS, state.sortAscending) { onEvent(ExamsUiEvent.SortChanged(ExamsSortField.STATUS)) }
                ), actionsLabel = "Actions", actionsWeight = .95f, actionsAlignment = Alignment.Center) },
                body = {
                    if (state.isLoading) AdminListTableShimmer(columns = columns, rowCount = state.pageSize)
                    else if (state.errorMessage.isNotBlank()) Box(Modifier.fillMaxWidth().weight(1f), contentAlignment = Alignment.Center) { AppDialogErrorMessage(state.errorMessage) }
                    else if (pageItems.isEmpty()) Box(Modifier.fillMaxWidth().weight(1f), contentAlignment = Alignment.Center) { Text("No exams found", color = AppNavy, fontWeight = FontWeight.Bold) }
                    else AdminListTableBody(items = pageItems, key = { it.id }, modifier = Modifier.fillMaxWidth().weight(1f)) { row ->
                        ExamTableRow(row, onView = { onEvent(ExamsUiEvent.ViewExamDetailsClicked(row)) }, onEdit = { onEvent(ExamsUiEvent.EditExamClicked(row)) }, onViewResults = { onEvent(ExamsUiEvent.ViewResultsClicked(row)) }, onEditResults = { onEvent(ExamsUiEvent.EditResultsClicked(row)) }, onInsights = { onEvent(ExamsUiEvent.ViewInsightsClicked(row)) }, onDelete = { deleteTarget = row })
                        HorizontalDivider(color = AppBorder)
                    }
                },
                footer = { AdminListFooter(pageText = pageFooterText(window, "exams"), pageIndex = window.pageIndex, pageCount = window.pageCount, onPageChanged = { onEvent(ExamsUiEvent.PageChanged(it)) }, pageSize = state.pageSize, rowsPerPageExpanded = rowsMenuExpanded, onRowsPerPageToggle = { rowsMenuExpanded = !rowsMenuExpanded }, onRowsPerPageDismiss = { rowsMenuExpanded = false }, onPageSizeChanged = { onEvent(ExamsUiEvent.PageSizeChanged(it)) }) }
            )
        },
        bottomAction = {
            AdminListPrimaryActionButton("Add Exam Details", painterResource(Res.drawable.plus), { addDialogVisible = true; onEvent(ExamsUiEvent.AddRequested) }, Modifier.padding(top = 4.dp))
        }
    )

    LaunchedEffect(state.saveGeneration) {
        if (state.saveGeneration > 0) {
            addDialogVisible = false
            editingExam = null
            onEvent(ExamsUiEvent.CloseEditExam)
        }
    }

    AddExamDialog(
        visible = addDialogVisible || editingExam != null || state.selectedEditExam != null,
        initialExam = state.selectedEditExam ?: editingExam,
        classOptions = state.classOptionIds,
        subjectOptions = state.subjectOptionIds,
        examTypeOptions = state.examTypeOptions,
        statusOptions = state.examStatusOptions,
        isSubjectsLoading = state.isLoadingSubjects,
        isSaving = state.isSaving || state.isLoadingEditExam,
        errorMessage = state.editExamError.ifBlank { state.saveErrorMessage },
        onDismiss = { addDialogVisible = false; editingExam = null; onEvent(ExamsUiEvent.CloseEditExam) },
        onClassSelected = { onEvent(ExamsUiEvent.ClassSelected(it)) },
        onAdd = { draft -> onEvent(ExamsUiEvent.CreateExam(draft)) },
        onEdit = { exam ->
            onEvent(
                ExamsUiEvent.UpdateExam(
                    id = exam.backendId,
                    name = exam.name,
                    type = exam.type,
                    classes = exam.classes,
                    startDate = exam.startDate,
                    endDate = exam.endDate,
                    status = exam.status,
                    maxMarks = exam.maxMarks,
                    hasOverallGrade = exam.hasOverallGrade,
                    hasOverallRank = exam.hasOverallRank,
                    hasOverallMarks = exam.hasOverallMarks,
                    subjectMarkConfigs = exam.subjectConfigs.map { (subjectId, config) ->
                        ExamSubjectDraft(
                            subjectId = subjectId,
                            internalMark = config.internalMark,
                            mainMark = config.mainMark,
                            totalMark = config.totalMark,
                            hasGrade = config.hasGrade,
                            hasRank = config.hasRank,
                            hasMarks = config.hasMarks
                        )
                    }
                )
            )
        }
    )
    ExamViewDialog(
        row = state.selectedExamDetails,
        subjectNamesById = state.subjectOptionIds.entries.associate { (name, id) -> id to name } + state.subjectNamesById,
        isLoading = state.isLoadingExamDetails,
        errorMessage = state.examDetailsError,
        onDismiss = { onEvent(ExamsUiEvent.CloseExamDetails) }
    )
    ExamResultsViewDialog(
        row = state.selectedResultsExam,
        results = state.results,
        isLoading = state.isLoadingResults,
        errorMessage = state.resultsError,
        isDownloading = state.isLoadingImportExport,
        downloadError = state.importExportError,
        onDownloadExcel = { examId -> onEvent(ExamsUiEvent.DownloadResultsExcelClicked(examId)) },
        onDismiss = { onEvent(ExamsUiEvent.CloseResults) }
    )
    deleteTarget?.let { exam ->
        AppConfirmationDialog(
            onDismiss = { if (!state.isSaving) deleteTarget = null },
            title = "Delete Exam?",
            subtitle = "This exam and its associated result records will be removed from active records.",
            bodyText = "Delete ${exam.name}?",
            confirmLabel = "Delete",
            tone = AppConfirmationDialogTone.Danger,
            confirmEnabled = !state.isSaving,
            onConfirm = {
                deleteTarget = null
                onEvent(ExamsUiEvent.DeleteClicked(exam.backendId))
            }
        )
    }
    ExamInsightsDialog(
        row = state.selectedInsightsExam,
        insights = state.insights,
        isLoading = state.isLoadingInsights,
        errorMessage = state.insightsError,
        onDismiss = { onEvent(ExamsUiEvent.CloseInsights) }
    )
    ExamResultsEntryDialog(
        row = state.selectedEditResultsExam,
        // Add Exam stores this backend lookup as subject name -> subject id.
        // Results headers are keyed by subject id, so reverse it at the UI boundary.
        subjectOptions = state.subjectOptionIds.entries.associate { (name, id) -> id to name } + state.subjectNamesById,
        subjectCodes = state.subjectCodesById,
        apiStudents = state.editResultsStudents,
        errorMessage = state.editResultsError,
        subjectNameError = state.editResultsSubjectNameError,
        saveErrorMessage = state.saveErrorMessage,
        isLoading = state.isLoadingEditResults,
        isSaving = state.isSaving,
        isLoadingImportExport = state.isLoadingImportExport,
        importExportError = state.importExportError,
        studentCount = state.editResultsStudentCount,
        onDismiss = { onEvent(ExamsUiEvent.CloseEditResults) },
        onDownloadTemplate = { examId -> onEvent(ExamsUiEvent.DownloadImportTemplateClicked(examId)) },
        onImportResults = { fileName, bytes -> onEvent(ExamsUiEvent.ImportExamResultsClicked(examId = state.selectedEditResultsExam?.backendId.orEmpty(), fileName = fileName, bytes = bytes)) },
        onSave = { status, results ->
            state.selectedEditResultsExam?.let { exam ->
                onEvent(ExamsUiEvent.SaveExamResultsClicked(exam.backendId, exam.classSectionId, status, results))
            }
        }
    )
}

@Composable
private fun RowScope.ExamMetric(title: String, value: String, subtitle: String, icon: org.jetbrains.compose.resources.DrawableResource, accent: Color, bg: Color) = AdminListSummaryCard(title, value, subtitle, painterResource(icon), accent, bg, AppMuted, modifier = Modifier.weight(1f))

private fun examColumns() = listOf(
    AdminListHeaderColumn("Exam ID", 1f), AdminListHeaderColumn("Exam Name", 1.55f), AdminListHeaderColumn("Exam Type", 1.2f), AdminListHeaderColumn("Classes", 1.35f), AdminListHeaderColumn("Start Date", 1.05f), AdminListHeaderColumn("Results Entered", 1.05f), AdminListHeaderColumn("Status", .9f), AdminListHeaderColumn("Actions", .95f, alignCenter = true)
)

@Composable
private fun ExamFilterPanel(state: ExamsUiState, onEvent: (ExamsUiEvent) -> Unit) {
    AppCard(Modifier.fillMaxWidth().border(1.dp, AppBorder, RoundedCornerShape(10.dp)), containerColor = Color.White) {
        Row(Modifier.fillMaxWidth().padding(16.dp), horizontalArrangement = Arrangement.spacedBy(14.dp), verticalAlignment = Alignment.Bottom) {
            Column(Modifier.weight(2f), verticalArrangement = Arrangement.spacedBy(7.dp)) {
                Text("Search by Exam Title", color = AppNavy, fontWeight = FontWeight.Bold, fontSize = 13.sp)
                BoxWithConstraints(Modifier.fillMaxWidth()) {
                    AdminListSearchField(state.searchQuery, { onEvent(ExamsUiEvent.SearchChanged(it)) }, "Search...", width = maxWidth, height = 44.dp, surfaceColor = Color.White, borderColor = AppBorder, textColor = AppNavy)
                }
            }
            ExamDropdown("Status", state.filters.status.ifBlank { "All Statuses" }, state.examStatusOptions, Modifier.weight(1f)) { onEvent(ExamsUiEvent.FiltersChanged(state.filters.copy(status = it))) }
            val selectedClass = state.classOptionIds.entries.firstOrNull { it.value == state.filters.classSectionId }?.key
            ExamDropdown("Class", selectedClass ?: "All Classes", state.classOptionIds.keys.sortedWith(compareBySmartText<String> { it }), Modifier.weight(1f), allOptionLabel = "All Classes") { selected ->
                val classSectionId = state.classOptionIds[selected].orEmpty()
                onEvent(ExamsUiEvent.FiltersChanged(state.filters.copy(classSectionId = classSectionId)))
            }
            AppOutlinedButton({ onEvent(ExamsUiEvent.FiltersReset) }, Modifier.width(116.dp).height(44.dp), colors = ButtonDefaults.outlinedButtonColors(contentColor = AppPrimary), shape = RoundedCornerShape(8.dp)) { Icon(painterResource(Res.drawable.refresh_cw), null, Modifier.size(17.dp), tint = AppPrimary); Spacer(Modifier.width(6.dp)); Text("Clear", color = AppPrimary) }
        }
    }
}

@Composable
private fun ExamDropdown(
    label: String,
    value: String,
    options: List<String>,
    modifier: Modifier,
    allOptionLabel: String = "All",
    onSelected: (String) -> Unit
) = AppLabeledDropdownField(label, value, options, onSelected, modifier, allOptionLabel = allOptionLabel)

@Composable
private fun RowScope.ExamsSummaryCardsShimmer() = AdminListSummaryCardsShimmer(
    cardCount = 4,
    cardHeight = 128.dp,
    cardShape = RoundedCornerShape(12.dp),
    contentPadding = PaddingValues(22.dp),
    iconSize = 70.dp,
    horizontalSpacing = 20.dp,
    titleWidth = 118.dp,
    titleHeight = 15.dp,
    valueWidth = 72.dp,
    valueHeight = 31.dp,
    subtitleWidth = 132.dp,
    subtitleHeight = 12.dp
)

@Composable
private fun ExamTableRow(row: ExamRowUi, onView: () -> Unit, onEdit: () -> Unit, onViewResults: () -> Unit, onEditResults: () -> Unit, onInsights: () -> Unit, onDelete: () -> Unit) {
    val isPublished = row.status.equals("Published", ignoreCase = true)
    Row(Modifier.fillMaxWidth().onViewDoubleClick(onView).heightIn(min = 52.dp).padding(horizontal = 24.dp, vertical = 4.dp), verticalAlignment = Alignment.CenterVertically) {
        AdminListTableBodyText(row.id, Modifier.weight(1f), fontWeight = FontWeight.Medium)
        AdminListTableBodyText(row.name, Modifier.weight(1.55f))
        AdminListTableBodyText(row.type, Modifier.weight(1.2f))
        AdminListTableBodyText(row.classes, Modifier.weight(1.35f))
        AdminListTableBodyText(row.startDate, Modifier.weight(1.05f))
        AdminListTableBodyText(row.resultStatus, Modifier.weight(1.05f))
        AdminListTableBodyText(row.status, Modifier.weight(.9f), color = AppPrimary)
        Box(Modifier.weight(.95f), contentAlignment = Alignment.Center) {
            var expanded by remember { mutableStateOf(false) }
            AdminListActionIcons(onView = { expanded = false; onView() }, onEdit = { expanded = false; onEdit() }) {
                IconButton(onClick = { expanded = true }, modifier = Modifier.size(32.dp)) { Icon(painterResource(Res.drawable.more_vertical), "More actions", tint = AppNavy, modifier = Modifier.size(16.dp)) }
                AdminRowActionMenu(
                    sections = buildList {
                        if (isPublished) {
                            add(listOf(AdminRowActionItem("View Results", { expanded = false; onViewResults() }, painterResource(Res.drawable.chart_square))))
                        }
                        add(listOf(AdminRowActionItem("Edit Results", { expanded = false; onEditResults() }, painterResource(Res.drawable.edit))))
                        if (isPublished) {
                            add(listOf(AdminRowActionItem("Exam Insights", { expanded = false; onInsights() }, painterResource(Res.drawable.health_chart))))
                        }
                        add(listOf(AdminRowActionItem("Delete Exam", { expanded = false; onDelete() }, painterResource(Res.drawable.trash), iconTint = AppDanger, labelColor = AppDanger)))
                    }.filter { it.isNotEmpty() },
                    onDismissRequest = { expanded = false },
                    expanded = expanded,
                    width = 240.dp
                )
            }
        }
    }
}
